import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthOrSession } from '../../lib/authOrSession';

interface RequireAuthProps {
  children: ReactNode;
}

/**
 * Guard für authentifizierungspflichtige Routen.
 *
 * Nutzt `useAuthOrSession()` als Single-Source-of-Truth, damit derselbe
 * Code-Pfad für Dev-Mock UND Production-Supabase funktioniert. In
 * Production verlangt der Hook eine echte Supabase-Session (siehe
 * `apps/web/src/lib/authOrSession.ts`).
 *
 * Verhalten:
 *   - Während `isLoading` (Production-Pfad: erste Session-Hydration) zeigen
 *     wir einen leeren Frame, damit der Login-Screen nicht kurz aufblitzt.
 *   - Ohne User → Redirect auf /login mit `from`-State, damit der Magic-Link
 *     den User zurück zur ursprünglich angefragten Route bringen kann.
 *   - Mit User (status 'authenticated' ODER 'pending') → durchlassen.
 */
export function RequireAuth({ children }: RequireAuthProps) {
  const { user, status, isLoading } = useAuthOrSession();
  const location = useLocation();

  // Während der initialen Session-Hydration in Production: leerer Frame.
  // Verhindert Login-Flicker, der dem User suggerieren würde, ausgeloggt
  // zu sein, obwohl supabase.auth.getSession() noch läuft.
  if (isLoading) {
    return null;
  }

  if (!user) {
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }

  if (status !== 'authenticated' && status !== 'pending') {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}
